import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../api/AxiosClient";
import {
  SupplierInsightsResponse,
  ContractDashboardResponse,
  ContractInsightsResponse,
  ExpiringContractsResponse,
  SourcingInsightsResponse,
  SourcingDashboardResponse,
  ProcurementHealthDashboardResponse,
} from "../types/contractDashboard";

// Async thunk to fetch supplier insights
export const fetchSupplierInsights = createAsyncThunk<
  SupplierInsightsResponse,
  { group_of_companies: string; contract_type: string },
  { rejectValue: string }
>(
  "contractDashboard/fetchSupplierInsights",
  async ({ group_of_companies, contract_type }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<SupplierInsightsResponse>(
        "/supplier-insights",
        { group_of_companies, contract_type },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch supplier insights",
      );
    }
  },
);

// Async thunk to fetch contract insights
export const fetchContractInsights = createAsyncThunk<
  ContractInsightsResponse,
  { group_of_companies: string; contract_type: string },
  { rejectValue: string }
>(
  "contractDashboard/fetchContractInsights",
  async ({ group_of_companies, contract_type }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        "/contract-dashboard-insights",
        { group_of_companies, contract_type },
      );

      // Ensure we return an array
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }

      // Handle case where data might be nested
      if (data && Array.isArray(data.insights)) {
        return data.insights;
      }

      console.error("Unexpected contract insights response format:", data);
      return [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch contract insights",
      );
    }
  },
);

// Async thunk to fetch contract dashboard
export const fetchContractDashboard = createAsyncThunk<
  ContractDashboardResponse,
  { group_of_companies: string; contract_type: string },
  { rejectValue: string }
>(
  "contractDashboard/fetchContractDashboard",
  async ({ group_of_companies, contract_type }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<ContractDashboardResponse>(
        "/contract-dashboard",
        { group_of_companies, contract_type },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch contract dashboard",
      );
    }
  },
);

// Async thunk to fetch expiring contracts
export const fetchExpiringContracts = createAsyncThunk<
  ExpiringContractsResponse,
  {
    page: number;
    limit: number;
    group_of_companies: string;
    contract_type: string;
  },
  { rejectValue: string }
>(
  "contractDashboard/fetchExpiringContracts",
  async (
    { page, limit, group_of_companies, contract_type },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosClient.post<ExpiringContractsResponse>(
        "/contract-dashboard-expiring",
        { page, limit, group_of_companies, contract_type },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch expiring contracts",
      );
    }
  },
);

// Async thunk to fetch sourcing insights
export const fetchSourcingInsights = createAsyncThunk<
  SourcingInsightsResponse,
  { group_of_companies: string; contract_type: string },
  { rejectValue: string }
>(
  "contractDashboard/fetchSourcingInsights",
  async ({ group_of_companies, contract_type }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<SourcingInsightsResponse>(
        "/sourcing-dashboard-insights",
        { group_of_companies, contract_type },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch sourcing insights",
      );
    }
  },
);

// Async thunk to fetch sourcing dashboard
export const fetchSourcingDashboard = createAsyncThunk<
  SourcingDashboardResponse,
  { group_of_companies: string; contract_type: string },
  { rejectValue: string }
>(
  "contractDashboard/fetchSourcingDashboard",
  async ({ group_of_companies, contract_type }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<SourcingDashboardResponse>(
        "/sourcing-dashboard",
        { group_of_companies, contract_type },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch sourcing dashboard",
      );
    }
  },
);

// Async thunk to fetch procurement health insights
export const fetchProcurementHealthInsights = createAsyncThunk<
  ContractInsightsResponse,
  { group_of_companies: string; contract_type: string },
  { rejectValue: string }
>(
  "contractDashboard/fetchProcurementHealthInsights",
  async ({ group_of_companies, contract_type }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        "/procurement-health-dashboard-insights",
        { group_of_companies, contract_type },
      );

      // Ensure we return an array
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }

      // Handle case where data might be nested
      if (data && Array.isArray(data.insights)) {
        return data.insights;
      }

      console.error(
        "Unexpected procurement health insights response format:",
        data,
      );
      return [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch procurement health insights",
      );
    }
  },
);

// Async thunk to fetch procurement health dashboard
export const fetchProcurementHealthDashboard = createAsyncThunk<
  ProcurementHealthDashboardResponse,
  { group_of_companies: string; contract_type: string },
  { rejectValue: string }
>(
  "contractDashboard/fetchProcurementHealthDashboard",
  async ({ group_of_companies, contract_type }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<ProcurementHealthDashboardResponse>(
        "/procurement-health-dashboard",
        { group_of_companies, contract_type },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch procurement health dashboard",
      );
    }
  },
);

interface ContractDashboardState {
  supplierInsights: SupplierInsightsResponse | null;
  contractInsights: ContractInsightsResponse | null;
  contractDashboard: ContractDashboardResponse | null;
  expiringContracts: ExpiringContractsResponse | null;
  expiringContractsPage: number;
  sourcingInsights: SourcingInsightsResponse | null;
  sourcingDashboard: SourcingDashboardResponse | null;
  procurementHealthInsights: ContractInsightsResponse | null;
  procurementHealthDashboard: ProcurementHealthDashboardResponse | null;
  loading: boolean;
  contractInsightsLoading: boolean;
  contractDashboardLoading: boolean;
  expiringContractsLoading: boolean;
  sourcingInsightsLoading: boolean;
  sourcingDashboardLoading: boolean;
  procurementHealthInsightsLoading: boolean;
  procurementHealthDashboardLoading: boolean;
  error: string | null;
}

const initialState: ContractDashboardState = {
  supplierInsights: null,
  contractInsights: null,
  contractDashboard: null,
  expiringContracts: null,
  expiringContractsPage: 1,
  sourcingInsights: null,
  sourcingDashboard: null,
  procurementHealthInsights: null,
  procurementHealthDashboard: null,
  loading: false,
  contractInsightsLoading: false,
  contractDashboardLoading: false,
  expiringContractsLoading: false,
  sourcingInsightsLoading: false,
  sourcingDashboardLoading: false,
  procurementHealthInsightsLoading: false,
  procurementHealthDashboardLoading: false,
  error: null,
};

const contractDashboardSlice = createSlice({
  name: "contractDashboard",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSupplierInsights: (state) => {
      state.supplierInsights = null;
      state.error = null;
      state.loading = false;
    },
    clearContractDashboard: (state) => {
      state.contractInsights = null;
      state.contractDashboard = null;
      state.expiringContracts = null;
      state.expiringContractsPage = 1;
      state.error = null;
      state.loading = false;
      state.contractInsightsLoading = false;
      state.contractDashboardLoading = false;
      state.expiringContractsLoading = false;
    },
    setExpiringContractsPage: (state, action) => {
      state.expiringContractsPage = action.payload;
    },
    clearSourcingDashboard: (state) => {
      state.sourcingInsights = null;
      state.sourcingDashboard = null;
      state.sourcingInsightsLoading = false;
      state.sourcingDashboardLoading = false;
      state.error = null;
    },
    clearProcurementHealthDashboard: (state) => {
      state.procurementHealthInsights = null;
      state.procurementHealthDashboard = null;
      state.procurementHealthInsightsLoading = false;
      state.procurementHealthDashboardLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupplierInsights.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierInsights.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierInsights = action.payload;
        state.error = null;
      })
      .addCase(fetchSupplierInsights.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch supplier insights";
      })
      .addCase(fetchContractInsights.pending, (state) => {
        state.contractInsightsLoading = true;
        state.error = null;
      })
      .addCase(fetchContractInsights.fulfilled, (state, action) => {
        state.contractInsightsLoading = false;
        state.contractInsights = action.payload;
        state.error = null;
      })
      .addCase(fetchContractInsights.rejected, (state, action) => {
        state.contractInsightsLoading = false;
        state.error = action.payload || "Failed to fetch contract insights";
      })
      .addCase(fetchContractDashboard.pending, (state) => {
        state.contractDashboardLoading = true;
        state.error = null;
      })
      .addCase(fetchContractDashboard.fulfilled, (state, action) => {
        state.contractDashboardLoading = false;
        state.contractDashboard = action.payload;
        state.error = null;
      })
      .addCase(fetchContractDashboard.rejected, (state, action) => {
        state.contractDashboardLoading = false;
        state.error = action.payload || "Failed to fetch contract dashboard";
      })
      .addCase(fetchExpiringContracts.pending, (state) => {
        state.expiringContractsLoading = true;
        state.error = null;
      })
      .addCase(fetchExpiringContracts.fulfilled, (state, action) => {
        state.expiringContractsLoading = false;
        state.expiringContracts = action.payload;
        state.error = null;
      })
      .addCase(fetchExpiringContracts.rejected, (state, action) => {
        state.expiringContractsLoading = false;
        state.error = action.payload || "Failed to fetch expiring contracts";
      })
      .addCase(fetchSourcingInsights.pending, (state) => {
        state.sourcingInsightsLoading = true;
        state.error = null;
      })
      .addCase(fetchSourcingInsights.fulfilled, (state, action) => {
        state.sourcingInsightsLoading = false;
        state.sourcingInsights = action.payload;
        state.error = null;
      })
      .addCase(fetchSourcingInsights.rejected, (state, action) => {
        state.sourcingInsightsLoading = false;
        state.error = action.payload || "Failed to fetch sourcing insights";
      })
      .addCase(fetchSourcingDashboard.pending, (state) => {
        state.sourcingDashboardLoading = true;
        state.error = null;
      })
      .addCase(fetchSourcingDashboard.fulfilled, (state, action) => {
        state.sourcingDashboardLoading = false;
        state.sourcingDashboard = action.payload;
        state.error = null;
      })
      .addCase(fetchSourcingDashboard.rejected, (state, action) => {
        state.sourcingDashboardLoading = false;
        state.error = action.payload || "Failed to fetch sourcing dashboard";
      })
      .addCase(fetchProcurementHealthInsights.pending, (state) => {
        state.procurementHealthInsightsLoading = true;
        state.error = null;
      })
      .addCase(fetchProcurementHealthInsights.fulfilled, (state, action) => {
        state.procurementHealthInsightsLoading = false;
        state.procurementHealthInsights = action.payload;
        state.error = null;
      })
      .addCase(fetchProcurementHealthInsights.rejected, (state, action) => {
        state.procurementHealthInsightsLoading = false;
        state.error =
          action.payload || "Failed to fetch procurement health insights";
      })
      .addCase(fetchProcurementHealthDashboard.pending, (state) => {
        state.procurementHealthDashboardLoading = true;
        state.error = null;
      })
      .addCase(fetchProcurementHealthDashboard.fulfilled, (state, action) => {
        state.procurementHealthDashboardLoading = false;
        state.procurementHealthDashboard = action.payload;
        state.error = null;
      })
      .addCase(fetchProcurementHealthDashboard.rejected, (state, action) => {
        state.procurementHealthDashboardLoading = false;
        state.error =
          action.payload || "Failed to fetch procurement health dashboard";
      });
  },
});

export const {
  clearError,
  clearSupplierInsights,
  clearContractDashboard,
  setExpiringContractsPage,
  clearSourcingDashboard,
  clearProcurementHealthDashboard,
} = contractDashboardSlice.actions;
export default contractDashboardSlice.reducer;
